// A Currency custom field's popover body on the Opportunities list: the design-system NumberField with the
// field's currency symbol on the side Forecast's currency list places it and 2 decimals, as on the record page
// (CustomFieldCurrencyInput). The NumberField reports an amount only when the box is left or Enter is pressed,
// so Update cannot know while an amount is typed whether it changed, and stays enabled: Update or Enter saves
// the latest amount, and closes without saving when it is unchanged. A press on Update can leave focus in the
// box, so Update first takes focus out of it, which makes the NumberField report what was typed. Saving an
// emptied box clears the value.
// An amount the service would refuse (more than 9 digits before the decimal point) says why on the field and
// is not saved.
import React, {useRef, useState} from 'react';
import {Button, EMPHASIS, NumberField} from '@forecast-it/design-system';
import {
	AMOUNT_DECIMALS,
	amountToStored,
	currencyEntry,
	isValidAmount,
	sameAmount,
	storedAmount,
} from '../../../../shared/util/customFieldValueValidation';
import {Actions, Body} from './editorLayout';
import {saveCustomFieldValue} from './saveCustomFieldValue';
import type {CustomFieldEditorProps} from './CustomFieldEditor';

export const CurrencyFieldEditor = ({node, def, current, onCommit, close, labels}: CustomFieldEditorProps): JSX.Element => {
	const {symbol, prefixed} = currencyEntry(def.currencyCode);
	const stored = storedAmount(current);
	const [amount, setAmount] = useState(stored);
	// The amount the box last reported. Enter reports it and saves in the same keystroke, before a re-render.
	const latest = useRef(stored);
	const [invalid, setInvalid] = useState(false);
	// The popover stays mounted through its exit animation, so a second press could otherwise save again.
	const saving = useRef(false);
	const body = useRef<HTMLDivElement>(null);
	const toStored = (value: number): string | null => (Number.isNaN(value) ? null : amountToStored(value));
	const isRefused = (value: number): boolean => {
		const next = toStored(value);
		return next !== null && !isValidAmount(next);
	};
	const report = (value: number) => {
		latest.current = value;
		setAmount(value);
		setInvalid(isRefused(value));
	};
	const save = () => {
		// Leaving the box makes the NumberField report what was typed, synchronously, before the amount is read.
		// Nothing happens when focus has already left it.
		body.current?.querySelector('input')?.blur();
		if (saving.current || isRefused(latest.current)) return;
		saving.current = true;
		if (!sameAmount(latest.current, stored)) {
			saveCustomFieldValue({node, def, value: toStored(latest.current), onCommit});
		}
		close();
	};
	// The NumberField handles Enter itself and does not let the keydown bubble; it calls onInputChange after
	// reporting the amount, on Enter and on leaving the box. Only Enter saves, and not one that confirms an IME
	// composition.
	const onInputChange = (e: {key?: string; nativeEvent?: {isComposing?: boolean}}) => {
		if (e?.key === 'Enter' && !e.nativeEvent?.isComposing) save();
	};
	return (
		<Body data-cy="cf-currency-editor" ref={body}>
			<NumberField
				data-cy="cf-currency-input"
				label={def.displayName}
				value={amount}
				onChange={report}
				onInputChange={onInputChange}
				prefixText={prefixed ? symbol : undefined}
				suffixText={prefixed ? undefined : symbol}
				minimumFractionDigits={AMOUNT_DECIMALS}
				maximumFractionDigits={AMOUNT_DECIMALS}
				align={NumberField.ALIGN.RIGHT}
				allowEmpty
				errorText={invalid ? labels.amountInvalid : undefined}
			/>
			<Actions>
				<Button emphasis={EMPHASIS.LOW} data-cy="cf-cancel" onPress={close}>
					{labels.cancel}
				</Button>
				<Button emphasis={EMPHASIS.HIGH} data-cy="cf-submit" onPress={save}>
					{labels.update}
				</Button>
			</Actions>
		</Body>
	);
};

export default CurrencyFieldEditor;
